import { useCallback, useEffect, useState } from "react"
import { postSubmit } from "@ui-seeker/utils"

import { ANNOUNCEMENTS_APIS } from "@constants"
import { announcementsSeenTick } from "@signals"

/**
 * Backs the bell: every announcement this user was entitled to see, read or not.
 *
 * Plain React state, not signals — nothing outside the bell reads this, and useState is
 * what guarantees the re-render when the list or the unread count changes.
 *
 * Failures are swallowed, same as the popup: the bell is the least important thing in the
 * header and must never get in the way of the dashboard.
 */
export default function useAnnouncementsHistory() {
    const [entries, setEntries] = useState([])
    const [unread, setUnread] = useState(0)
    const [isLoading, setIsLoading] = useState(false)

    const reload = useCallback(() => {
        setIsLoading(true)
        return postSubmit(ANNOUNCEMENTS_APIS.HISTORY, {})
            .then(res => {
                setEntries(res?.data ?? [])
                setUnread(res?.unread ?? 0)
            })
            .catch(() => {
                setEntries([])
                setUnread(0)
            })
            .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => { reload() }, [reload])

    // Also when the popup marks one as read: it is the only other thing that changes the
    // unread count, and it happens while the badge is on screen.
    useEffect(() => {
        if (announcementsSeenTick.value === 0) return
        reload()
    }, [announcementsSeenTick.value, reload])

    /** Opening one counts as reading it: the badge goes down and the popup will not insist
     *  on an announcement the user has already opened here. */
    const open = announcementID =>
        postSubmit(ANNOUNCEMENTS_APIS.DETAIL, { announcementID })
            .then(detail => {
                postSubmit(ANNOUNCEMENTS_APIS.SEEN, { announcementID })
                    .then(() => reload())
                    .catch(() => {})
                return detail
            })

    /** Marking one read without opening it, from the row's hover action.
     *
     *  Optimistic: the dot clears now and the server is reconciled after. A list that waits
     *  for a round trip to clear a dot feels broken even when it is working. */
    const markSeen = announcementID => {
        setEntries(previous => previous.map(entry => (
            entry.announcementID === announcementID ? { ...entry, seen: true } : entry
        )))
        setUnread(previous => Math.max(0, previous - 1))

        return postSubmit(ANNOUNCEMENTS_APIS.SEEN, { announcementID })
            .then(() => reload())
            .catch(() => reload())
    }

    /** One `/seen` per unread entry rather than a bulk endpoint: `/seen` is an idempotent
     *  upsert and the unread count is small by definition — nobody sits on fifty unread
     *  announcements. A dedicated endpoint would buy a round trip and cost a second writer
     *  of the same table. */
    const markAllSeen = () => {
        const unseen = entries.filter(entry => !entry.seen)
        if (unseen.length === 0) return Promise.resolve()

        return Promise.all(
            unseen.map(entry =>
                postSubmit(ANNOUNCEMENTS_APIS.SEEN, { announcementID: entry.announcementID })
                    .catch(() => {}),
            ),
        ).then(() => reload())
    }

    return { entries, unread, isLoading, reload, open, markSeen, markAllSeen }
}
